import React from "react";
import { Table, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";

const AVATAR_HUES = [210, 340, 150, 30, 265, 190, 95, 5];

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

const hueFor = (name) => {
  let hash = 0;
  const value = String(name);
  for (let i = 0; i < value.length; i++) hash = (hash * 31 + value.charCodeAt(i)) | 0;
  return AVATAR_HUES[Math.abs(hash) % AVATAR_HUES.length];
};

const formatDate = (iso) =>
  iso
    ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

const SOURCE = {
  active: { label: "Live", color: "var(--rd-green-solid)", icon: null },
  archive: { label: "Archived", color: "var(--rd-text-muted)", icon: "mdi-archive-outline" },
  snapshot: { label: "Snapshot", color: "var(--rd-amber-solid)", icon: "mdi-camera-outline" },
};

const CONTACT_SOURCE_TITLE = {
  mastersheet: "Contact details from the mastersheet occupant record",
  lease: "Contact details from the signed lease",
  both: "Contact details from the mastersheet occupant record and the signed lease",
};

const copy = (value) => {
  if (!value || !navigator?.clipboard) return;
  navigator.clipboard.writeText(value);
};

const rowAsText = (row) =>
  [
    row.buildingName || "",
    row.unitNo,
    row.name,
    row.role === "tenant" ? "Tenant" : "Occupant",
    row.email || "",
    row.phone || "",
    row.moveIn || "",
    row.moveOut || "Current",
    row.status === "current" ? "Current" : "Past",
  ].join("\t");

const Avatar = ({ name }) => (
  <span
    className="rd-avatar"
    style={{ background: `hsl(${hueFor(name)} 58% 45%)` }}
    aria-hidden="true"
  >
    {initials(name)}
  </span>
);

const Resident = ({ row }) => (
  <div className="d-flex align-items-center gap-2">
    <Avatar name={row.name} />
    <div className="d-flex flex-wrap align-items-center gap-2">
      <span className="fw-medium">{row.name}</span>
      <span className="rd-role-pill">{row.role === "tenant" ? "Tenant" : "Occupant"}</span>
    </div>
  </div>
);

const Unit = ({ row, showBuilding }) => (
  <>
    <span className="rd-unit-chip">{row.unitNo}</span>
    {showBuilding && row.buildingName && <div className="rd-unit-building">{row.buildingName}</div>}
  </>
);

const ContactLinks = ({ row }) => (
  <div
    className="rd-contact d-flex flex-column"
    title={CONTACT_SOURCE_TITLE[row.contactSource] || undefined}
  >
    {row.email ? (
      <span className="d-flex align-items-center gap-1">
        <a href={`mailto:${row.email}`}>{row.email}</a>
        <button
          type="button"
          className="rd-copy"
          aria-label={`Copy email address for ${row.name}`}
          onClick={() => copy(row.email)}
        >
          <i className="mdi mdi-content-copy" />
        </button>
      </span>
    ) : (
      <span className="text-muted">—</span>
    )}
    <span className="d-flex align-items-center gap-1 small">
      <i className="mdi mdi-phone-outline text-muted" aria-hidden="true" />
      {row.phone ? (
        <>
          <a href={`tel:${row.phone.replace(/\s+/g, "")}`}>{row.phone}</a>
          <button
            type="button"
            className="rd-copy"
            aria-label={`Copy phone number for ${row.name}`}
            onClick={() => copy(row.phone)}
          >
            <i className="mdi mdi-content-copy" />
          </button>
        </>
      ) : (
        <span className="text-muted">—</span>
      )}
    </span>
  </div>
);

const Tenancy = ({ row }) => (
  <div className="d-flex flex-column">
    <span>
      {formatDate(row.moveIn) || "—"} <span className="text-muted">→</span>{" "}
      {row.moveOut ? formatDate(row.moveOut) : <strong>Current</strong>}
    </span>
    {(row.leaseStart || row.leaseEnd) && (
      <span className="small text-muted">
        Lease {formatDate(row.leaseStart) || "—"} – {formatDate(row.leaseEnd) || "—"}
      </span>
    )}
  </div>
);

const StatusPill = ({ status }) => (
  <span className={`rd-status rd-status--${status}`}>
    {status === "current" ? "Current" : "Past"}
  </span>
);

const SourceLabel = ({ source }) => {
  const meta = SOURCE[source] || SOURCE.active;
  return (
    <span className="rd-source">
      {meta.icon ? (
        <i className={`mdi ${meta.icon}`} aria-hidden="true" />
      ) : (
        <span className="rd-source-dot" style={{ background: meta.color }} aria-hidden="true" />
      )}
      {meta.label}
    </span>
  );
};

const RowMenu = ({ row }) => (
  <UncontrolledDropdown>
    <DropdownToggle
      tag="button"
      type="button"
      className="rd-row-menu"
      aria-label={`Actions for ${row.name}`}
    >
      <i className="mdi mdi-dots-vertical font-size-16" />
    </DropdownToggle>
    {/* reactstrap 8 ships Bootstrap 4's `right` prop; this project is on Bootstrap 5. */}
    <DropdownMenu className="dropdown-menu-end">
      <DropdownItem disabled={!row.email} onClick={() => copy(row.email)}>
        <i className="mdi mdi-email-outline me-2" />
        Copy email
      </DropdownItem>
      <DropdownItem disabled={!row.phone} onClick={() => copy(row.phone)}>
        <i className="mdi mdi-phone-outline me-2" />
        Copy phone
      </DropdownItem>
      <DropdownItem divider />
      <DropdownItem onClick={() => copy(rowAsText(row))}>
        <i className="mdi mdi-table-arrow-right me-2" />
        Copy row
      </DropdownItem>
    </DropdownMenu>
  </UncontrolledDropdown>
);

const SortHeader = ({ label, column, sort, onSort }) => (
  <th
    scope="col"
    aria-sort={sort.key === column ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
  >
    <button type="button" className="rd-sort-btn" onClick={() => onSort(column)}>
      {label}
      {sort.key === column && (
        <i className={`mdi mdi-chevron-${sort.dir === "asc" ? "up" : "down"}`} aria-hidden="true" />
      )}
    </button>
  </th>
);

const rowKey = (row, i) => `${row.buildingName}-${row.unitNo}-${row.name}-${row.moveIn}-${i}`;

const ResidentTable = ({ rows, sort, onSort, showBuilding }) => (
  <>
    <div className="d-none d-lg-block rd-table-wrap">
      <Table responsive className="rd-table mb-0">
        <thead>
          <tr>
            <SortHeader label="Resident" column="name" sort={sort} onSort={onSort} />
            <SortHeader label="Unit" column="unitNo" sort={sort} onSort={onSort} />
            <th scope="col">Contact</th>
            <SortHeader label="Tenancy" column="moveIn" sort={sort} onSort={onSort} />
            <SortHeader label="Status" column="status" sort={sort} onSort={onSort} />
            <th scope="col">Source</th>
            <th scope="col">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={rowKey(row, i)}>
              <td>
                <Resident row={row} />
              </td>
              <td>
                <Unit row={row} showBuilding={showBuilding} />
              </td>
              <td>
                <ContactLinks row={row} />
              </td>
              <td>
                <Tenancy row={row} />
              </td>
              <td>
                <StatusPill status={row.status} />
              </td>
              <td>
                <SourceLabel source={row.source} />
              </td>
              <td className="text-end">
                <RowMenu row={row} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>

    <div className="d-lg-none rd-card-list">
      {rows.map((row, i) => (
        <div className="rd-card" key={`c-${rowKey(row, i)}`}>
          <div className="d-flex align-items-start gap-2 mb-2">
            <Avatar name={row.name} />
            <div className="d-flex flex-column flex-grow-1">
              <span className="fw-medium">{row.name}</span>
              <span className="rd-role-pill align-self-start mt-1">
                {row.role === "tenant" ? "Tenant" : "Occupant"}
              </span>
            </div>
            <StatusPill status={row.status} />
            <RowMenu row={row} />
          </div>
          <div className="mb-2">
            <span className="rd-unit-chip">{row.unitNo}</span>
            {row.buildingName && (
              <span className="rd-unit-building d-inline ms-2">{row.buildingName}</span>
            )}
          </div>
          <ContactLinks row={row} />
          <div className="mt-2 small d-flex justify-content-between align-items-end gap-2">
            <Tenancy row={row} />
            <SourceLabel source={row.source} />
          </div>
        </div>
      ))}
    </div>
  </>
);

export { ResidentTable };
